/**
 * PR-V0c — draft preview layer.
 *
 * Mounted as a child of `<Canvas>` (R3F context). Listens to the
 * `draftPreviewEntity` prop and rebuilds the draft preview group
 * inside the THREE scene whenever the prop changes. When the prop
 * becomes `null` the group is cleared and disposed.
 *
 * # AutoCAD parity
 *
 * AutoCAD draws the draft preview as a "rubber band" entity in a
 * dimmed yellow / white shade that follows the cursor while a draft
 * command is active (LINE second-point pick, CIRCLE radius drag,
 * RECTANG diagonal drag, etc.). The preview is NOT part of the
 * persistent scene — it is ephemeral, redrawn on every cursor move,
 * and discarded when the command commits or the user presses Esc.
 *
 * # Implementation
 *
 *   - We feed `draftPreviewEntity` through the same `entityToRenderable`
 *     adapter pass the persistent entities use, then walk the
 *     resulting `RenderableEntity` and pick the draft-preview-mesh
 *     primitive (line / circle / polyline) that matches the geometry
 *     kind. The handle from `createDraftPreviewMesh` does the rest.
 *
 *   - The draft preview is intentionally NOT inserted into the
 *     spatial index — it is not a real entity, must not participate
 *     in hit-test, and must not show grips.
 *
 *   - The handle is allocated lazily on first non-null prop and torn
 *     down on unmount. Re-creating the handle on every prop change
 *     would be wasteful (the inner `update` reuses meshes).
 *
 * # DRAFT-DOCUMENT-FRAME — why there is no `applyOffset` in this file
 *
 * The draft session works in DOCUMENT coordinates
 * (`pickLoop/interactivePickLoop.ts` converts at the pointer emit), so the
 * preview entity `buildDraftPreviewEntity` produces carries document values
 * and needs exactly ONE `applyOffset` before it reaches the GPU. It already
 * gets it: this layer feeds the preview through the SAME `entityToRenderable`
 * adapter pass the persistent entities use, and the shared builders
 * (`adapters/sceneGeometryBuilders.ts`, `adapters/geom/*`) call `applyOffset`
 * on every absolute position — line endpoints, circle/arc centres, polyline
 * vertices and dimension primitives, i.e. every geometry kind
 * `renderableToDraftUpdate` below can emit. Adding a second one here would
 * paint the band a whole session origin away, which is exactly what the
 * PREVIOUS convention did: while the session held offset-world points the
 * adapter offset them a second time (`p − 2·O`).
 *
 * The one place that does need a conversion is the in-VP `previewMapping`,
 * because its `modelToPaper` affine runs AFTER the adapter offset and mixes
 * in the document-frame `viewport.viewCenter` — see
 * `draftPreviewMapping.ts`'s `mapPoint`.
 */

import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useThree } from "@react-three/fiber";

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import {
  createDraftPreviewMesh,
  type DraftPreviewMeshHandle,
  type DraftPreviewUpdate,
} from "../grips/draftPreviewMesh";
import { entityToRenderable, type AdapterContext } from "../adapters";
import { buildLayerColorMapFromLayerDefinitions } from "../rendering/colorResolution";
import { useEditorStore, type EditorStore } from "../../state/editorStore";

/** AutoCAD draft preview color — dimmed white / pale yellow. */
const DRAFT_PREVIEW_COLOR = "#ffd98a";

/**
 * fix/bylayer-colour-never-resolves-on-canvas — stable empty-array
 * sentinel for the `styleTables.layers` subscription below (Phase 1.k
 * precedent, `SceneContent.tsx`'s `EMPTY_LAYER_STATES`): a fresh `?? []`
 * literal minted on every store read defeats `useSyncExternalStore`'s
 * snapshot bail-out.
 */
const EMPTY_DRAFT_PREVIEW_LAYERS: ReadonlyArray<{
  name: string;
  color?: number;
}> = [];

/**
 * MC4 Slice 4 — draft-preview coordinate mapping.
 *
 * When a draft command runs INSIDE a paper-space viewport (MSPACE_VIA_VP),
 * the preview entity is authored in the VP's MODEL coordinates, but the
 * rubber band is painted on the PAPER camera (`draftPreviewMesh` is
 * parented to the same scene the paper sheet renders on). Without a
 * transform, the model coords would be interpreted as paper coords and the
 * rubber band would land at the wrong place / scale, OUTSIDE the VP rect.
 *
 * This mapping carries the same MODEL→PAPER affine the S3 OSNAP marker uses
 * to map its snap glyph back to paper (`modelToPaper` bound to the active VP
 * + paper origin). `mapPoint` maps every preview vertex; `radiusScale` is
 * the VP's isotropic model→paper scale (`vp.height / vp.viewHeight`) applied
 * to circle radii. The VP transform is a rotation + uniform scale, so a
 * circle stays a circle: center↦`mapPoint(center)`, radius↦`radius·scale`
 * is exact.
 *
 * When omitted (the common case — drafting in plain model / paper space, or
 * over a LOCKED VP) the preview path is byte-identical to pre-S4 behaviour.
 */
export interface DraftPreviewMapping {
  /** MODEL→PAPER per-vertex map (`modelToPaper` bound to the active VP + origin). */
  mapPoint: (p: { x: number; y: number }) => { x: number; y: number };
  /** MODEL→PAPER isotropic scale for radii (`vp.height / vp.viewHeight`). */
  radiusScale: number;
  /**
   * MC4 clip slice — the active VP's axis-aligned paper rect (paper/world
   * coords), the SAME bounds `pointInViewport` uses. Forwarded verbatim to
   * `DraftPreviewMeshHandle.setClipRect` so the rubber band is clipped to the
   * VP boundary (AutoCAD parity: a draft band never extends past the viewport
   * edge onto the sheet). Non-null whenever the mapping is non-null (same
   * gate). Plain numbers — the mesh handle owns the derived `THREE.Plane`
   * instances and mutates them in place on rect change (#234 @codex P2: no
   * per-pointer-move Plane allocation, no material recompile while only the
   * rubber-band endpoint moves), keeping this mapping module THREE-free.
   */
  readonly clipRect: {
    readonly minX: number;
    readonly maxX: number;
    readonly minY: number;
    readonly maxY: number;
  };
}

export interface DraftPreviewLayerProps {
  /** Entity to render as draft preview. `null` clears the layer. */
  readonly draftPreviewEntity: EntityPreview | null | undefined;
  /**
   * MC4 S4 — optional MODEL→PAPER mapping for an in-VP draft. Non-null ONLY
   * when drafting inside an unlocked active viewport (MSPACE_VIA_VP); maps
   * each preview vertex back to paper so the rubber band lands inside the VP
   * rect at the right scale. `null` / undefined → no transform (identity).
   */
  readonly previewMapping?: DraftPreviewMapping | null;
}

/**
 * Convert a renderable's geometry into a draft preview mesh update.
 * Returns `null` for unsupported kinds so the caller can fall back to
 * clearing the preview without crashing.
 *
 * `mapping` (MC4 S4): when present, every emitted vertex is mapped through
 * `mapping.mapPoint` (MODEL→PAPER) and circle radii are scaled by
 * `mapping.radiusScale`, so an in-VP draft's rubber band paints inside the
 * VP rect on the paper camera. When null/undefined the output is
 * byte-identical to the pre-S4 behaviour (no transform).
 */
export function renderableToDraftUpdate(
  renderable: import("../types").RenderableEntity,
  mapping?: DraftPreviewMapping | null,
): DraftPreviewUpdate | null {
  // Identity map when no mapping supplied — keeps the non-VP path
  // byte-for-byte unchanged (same object shapes, same numbers).
  const map = mapping
    ? mapping.mapPoint
    : (p: { x: number; y: number }) => ({ x: p.x, y: p.y });
  const radiusScale = mapping ? mapping.radiusScale : 1;
  const g = renderable.geometry;
  switch (g.kind) {
    case "line": {
      const a = map({ x: g.a.x, y: g.a.y });
      const b = map({ x: g.b.x, y: g.b.y });
      return {
        kind: "line",
        a,
        b,
        colorHex: DRAFT_PREVIEW_COLOR,
      };
    }
    case "circle": {
      const center = map({ x: g.center.x, y: g.center.y });
      return {
        kind: "circle",
        center,
        radius: g.radius * radiusScale,
        colorHex: DRAFT_PREVIEW_COLOR,
      };
    }
    case "polyline":
      return {
        kind: "polyline",
        points: g.points.map((p) => map({ x: p.x, y: p.y })),
        closed: g.closed,
        colorHex: DRAFT_PREVIEW_COLOR,
      };
    case "arc": {
      // Tessellate arc into a polyline preview. The draft preview
      // mesh helper does not have a dedicated arc primitive; a
      // 64-segment polyline matches the screen resolution at
      // typical zoom levels and matches the AutoCAD rubber-band
      // smoothness.
      //
      // S4: the tessellation runs in MODEL space (model radius/center) so
      // the curve is sampled correctly; only the FINAL points are mapped to
      // paper. Mapping a uniform-scale + rotation per-vertex preserves the
      // arc shape exactly (no double-apply — radius/center are NOT scaled
      // before tessellation).
      const segments = 64;
      const pts: { x: number; y: number }[] = [];
      const startAngle = g.start;
      let endAngle = g.end;
      // AutoCAD arcs are always CCW; if g.ccw is false we sweep
      // the opposite direction.
      if (!g.ccw && endAngle > startAngle) {
        endAngle = endAngle - Math.PI * 2;
      }
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const angle = startAngle + (endAngle - startAngle) * t;
        pts.push(
          map({
            x: g.center.x + g.radius * Math.cos(angle),
            y: g.center.y + g.radius * Math.sin(angle),
          }),
        );
      }
      return {
        kind: "polyline",
        points: pts,
        closed: false,
        colorHex: DRAFT_PREVIEW_COLOR,
      };
    }
    case "dimension": {
      // PR-F P1 — DIMLINEAR rubber-band. Walk the primitive list and
      // emit a disjoint-segments update from line-class primitives
      // (extension-line, dim-line, leader). Arrow / tick / text /
      // arc primitives are intentionally skipped — rubber-band is a
      // lightweight per-frame preview that only needs to show the
      // user the dimension's overall layout. After commit, the full
      // mesh + text pipeline kicks in via SceneBulkLoad.
      const segments: {
        a: { x: number; y: number };
        b: { x: number; y: number };
      }[] = [];
      for (const prim of g.primitives) {
        if (
          prim.kind === "extension-line" ||
          prim.kind === "dim-line" ||
          prim.kind === "leader"
        ) {
          segments.push({
            a: map({ x: prim.a.x, y: prim.a.y }),
            b: map({ x: prim.b.x, y: prim.b.y }),
          });
        }
      }
      if (segments.length === 0) return null;
      return {
        kind: "segments",
        segments,
        colorHex: DRAFT_PREVIEW_COLOR,
      };
    }
    default:
      return null;
  }
}

/**
 * R3F-side draft preview layer. Mounted inside `<Canvas>`. Owns the
 * draft preview mesh handle (a THREE.Group attached to the scene) and
 * keeps it in sync with the `draftPreviewEntity` prop.
 *
 * Returns `null` — meshes are added directly to the scene via the
 * handle's group reference. R3F is happy with `null` children inside
 * `<Canvas>`.
 */
export function DraftPreviewLayer(props: DraftPreviewLayerProps): null {
  const { scene, invalidate } = useThree((s) => ({
    scene: s.scene,
    invalidate: s.invalidate,
  }));
  const handleRef = useRef<DraftPreviewMeshHandle | null>(null);

  // fix/bylayer-colour-never-resolves-on-canvas — layer table subscription,
  // the SAME `styleTables.layers` field `SceneContent.tsx`'s `layerStates`
  // reads. A BYLAYER draft preview (e.g. an in-flight LINE drawn on a
  // non-default current layer) must resolve `entityToRenderable`'s BYLAYER
  // branch through this table too — an always-empty map here reproduced the
  // white-on-white bug for any renderable geometry kind that does not
  // override colour after the adapter call (see `renderableToDraftUpdate`;
  // most kinds paint the fixed `DRAFT_PREVIEW_COLOR` rubber band and are
  // unaffected, but this keeps `ctx` correct for the adapter contract
  // instead of relying on that override as an accidental safety net).
  const rawLayers = useEditorStore(
    (s: EditorStore) =>
      (s.geometryDocument.styleTables?.layers ??
        EMPTY_DRAFT_PREVIEW_LAYERS) as ReadonlyArray<{
        name: string;
        color?: number;
      }>,
  );
  const layerColors = useMemo(
    () => buildLayerColorMapFromLayerDefinitions(rawLayers),
    [rawLayers],
  );

  // Allocate the draft preview handle once on mount. Tear down on
  // unmount. The handle's `update` / `clear` calls below mutate the
  // existing meshes without further allocation.
  useEffect(() => {
    handleRef.current = createDraftPreviewMesh(scene);
    return () => {
      handleRef.current?.dispose();
      handleRef.current = null;
    };
  }, [scene]);

  // Sync with prop. Re-runs on every prop change.
  //
  // S4: `props.previewMapping` is in the dependency array so the rubber band
  // re-maps when the active VP's view changes mid-draft (pan/zoom inside the
  // VP changes the model→paper transform; the memoized mapping identity flips
  // and this effect re-runs with the fresh transform).
  //
  // Clip slice: the same `props.previewMapping.clipRect` re-clips the band to
  // the VP rect; on every clear path we also drop the planes so a stale clip
  // cannot persist after the draft / VP context ends.
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) {
      writeDraftPreviewMirror(null, null);
      return;
    }
    const clipRect = props.previewMapping?.clipRect ?? null;
    const entity = props.draftPreviewEntity;
    if (!entity) {
      handle.clear();
      handle.setClipRect(null);
      writeDraftPreviewMirror(null, null);
      invalidate();
      return;
    }
    // Convert to renderable. The preview entity goes through the LEGACY
    // bag adapter pass; the only difference from a real entity is we do
    // NOT register it in the spatial index.
    //
    // DEFUSE S11 note - this channel deliberately STAYS on the legacy
    // `entityToRenderable`: an in-flight draft is a command-engine bag
    // preview (`previewBuilders.ts`) with NO typed `DrawingEntity` in the
    // document until commit, so the typed-first route
    // (`adapters/typedFirstRenderable.ts`) has nothing to resolve. Typed
    // draft previews are command-engine scope (plan §6 S15/S16); this is
    // one of the two reasons the legacy per-kind adapters survive S11.
    const ctx: AdapterContext = {
      layerColors,
      cameraZoom: 1,
      zOrderIndex: 0,
    };
    const renderable = entityToRenderable(entity, ctx);
    if (!renderable) {
      // Unsupported preview kind — clear and bail.
      handle.clear();
      handle.setClipRect(null);
      writeDraftPreviewMirror(null, null);
      invalidate();
      return;
    }
    const update = renderableToDraftUpdate(renderable, props.previewMapping);
    if (!update) {
      handle.clear();
      handle.setClipRect(null);
      writeDraftPreviewMirror(null, null);
      invalidate();
      return;
    }
    handle.update(update);
    // Clip slice — clip the rubber band to the active VP's paper rect. When an
    // in-VP mapping is present, the band is already in PAPER/world coords, so
    // the handle's 4 axis-aligned world-space clip planes (derived from
    // `clipRect`) bound it to the VP rect exactly. `clipIntersection = false`
    // on the material ⇒ a fragment must be inside ALL 4 planes (intersection)
    // ⇒ inside the rect. When no mapping (plain model / paper draft, or a
    // LOCKED VP) → null → unclipped, byte-identical to pre-clip behaviour.
    //
    // #234 @codex P2 — this effect re-runs on EVERY pointer move (the
    // `draftPreviewEntity` dep), but the rect values only change on a VP
    // move/zoom. We pass the plain rect; the handle no-ops on identical
    // values, mutates its owned Planes in place on a value change, and bumps
    // `material.needsUpdate` (program recompile) ONLY on a null↔rect toggle —
    // see `setClipRect` for the three.js uniform-vs-define rationale.
    handle.setClipRect(clipRect);
    // MC4 S4 — read-only E2E observability mirror (no production behaviour
    // depends on it). The post-mapping `DraftPreviewUpdate` (already in PAPER
    // coords when an in-VP mapping was applied) is the only window-observable
    // channel for the rubber band — the draft preview mesh is a bare THREE
    // group, not on any store. Same legitimate hook pattern as S3's
    // `window.__edit_dwg_overlay` / `window.__cam`. The clipRect is mirrored
    // alongside so a headless scenario can locate the rect edges to sample
    // inside-vs-outside pixels.
    writeDraftPreviewMirror(update, clipRect);
    invalidate();
  }, [
    props.draftPreviewEntity,
    props.previewMapping,
    invalidate,
    layerColors,
  ]);

  return null;
}

/** Read-only mirror of the active clip rect (paper coords) for headless gates. */
export interface DraftPreviewClipRectMirror {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/**
 * MC4 S4 — read-only E2E mirror of the last post-mapping draft preview update
 * onto `window.__edit_dwg_draftpreview`. Headless scenarios assert the mapped
 * rubber-band vertices land at the expected PAPER coords inside the VP rect.
 * No production code reads this — it is purely an observability channel, the
 * same pattern as `window.__edit_dwg_overlay` (S3 OSNAP marker) and
 * `window.__cam`.
 *
 * Clip slice: also mirror the active `clipRect` (paper coords) onto
 * `window.__edit_dwg_draftclip` so the pixel-sampling scenario can locate the
 * VP rect edge and probe a point just inside vs just outside it. Read-only; no
 * production code reads it. (Clipping is a GPU fragment effect — the UPDATE
 * vertices are unchanged by it, so the rect mirror only LOCATES the edge; the
 * actual clip proof is the framebuffer pixel sample in the scenario.)
 */
function writeDraftPreviewMirror(
  update: DraftPreviewUpdate | null,
  clipRect: DraftPreviewClipRectMirror | null,
): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    __edit_dwg_draftpreview?: DraftPreviewUpdate | null;
    __edit_dwg_draftclip?: DraftPreviewClipRectMirror | null;
  };
  w.__edit_dwg_draftpreview = update;
  w.__edit_dwg_draftclip = clipRect;
}

/**
 * Test-only — exported for the unit test to assert dispose-on-null
 * behavior without mounting the full Canvas tree.
 */
export const __DRAFT_PREVIEW_COLOR_FOR_TESTS = DRAFT_PREVIEW_COLOR;
